import React from 'react';
import { Link } from 'react-router-dom';
import './StaticLegalPages.css'; // Shared CSS for all legal text pages

const RefundPage = () => {
  return (
    <div className="static-page-container">
      <h1>Refund & Cancellation Policy</h1>
      <p><strong>Last Updated: January 20, 2026</strong></p>
      
      <div className="policy-binding-notice" style={{ 
              backgroundColor: '#f7f5f2', 
              padding: '15px', 
              borderLeft: '4px solid #8A181A',
              marginBottom: '20px',
              fontSize: '0.95rem'
            }}>
              <p>
                <strong>Notice:</strong> By creating an account, signing up, or using FullMockup, 
                you acknowledge that you have read, understood, and agree to be bound by this 
                Refund & Cancellation policy and our <Link to="/terms">Terms & Conditions</Link>.
              </p>
        </div>

      <h2>1. Introduction</h2>
      <p>
        Thank you for choosing <strong>FullMockup</strong> ("we," "us," or "our"). We provide a 
        Software-as-a-Service (SaaS) platform that allows users to generate high-fidelity 
        apparel mockups using advanced physics-based rendering engines.
      </p>
      <p>
        By subscribing to our plans or purchasing services on fullmockup.com, you agree to 
        the terms outlined in this Refund & Cancellation Policy.
      </p>

      <h2>2. Digital Nature of Goods</h2>
      <p>
        FullMockup provides immediate access to digital tools and server-side computing 
        resources. Due to the nature of digital goods and the immediate costs we incur 
        for server usage (GPU/CPU time and Storage), <strong>all sales are generally final 
        and non-refundable</strong>, except as expressly stated in this policy.
      </p>

      <h2>3. Subscription Cancellation</h2>
      <p>
        You may cancel your subscription (Lite, Pro, or Agency) at any time. We believe 
        in "No-Lock-In" contracts.
      </p>
      <ul>
        <li>
          <strong>How to Cancel:</strong> You can cancel your subscription directly through 
          your Account Dashboard &gt; Billing & Usage section.
        </li>
        <li>
          <strong>Effect of Cancellation:</strong> Your cancellation will go into effect at 
          the end of your current billing cycle.
        </li>
        <li>
          <strong>Example:</strong> If you are billed on the 5th of the month and cancel 
          on the 10th, you will retain full access to your plan features until the 5th 
          of the following month.
        </li>
        <li>
          <strong>No Partial Refunds:</strong> We do not offer pro-rated refunds for 
          unused time in the billing period.
        </li>
      </ul>

      <h2>4. Refund Policy on Credits & Usage</h2>
      <p>
        Our mockups are generated using a proprietary, self-hosted physics engine. Every 
        generation consumes real-time server resources (Compute & Bandwidth).
      </p>

      <h3>A. Non-Refundable Scenarios</h3>
      <p>Credits deducted for a generation are non-refundable in the following cases:</p>
      <ul>
        <li>
          <strong>Subjective Quality:</strong> You are dissatisfied with the aesthetic 
          style or artistic output of the mockup.
        </li>
        <li>
          <strong>User Error:</strong> You uploaded a low-resolution input file, an 
          incorrect file format, or selected the wrong garment options.
        </li>
        <li>
          <strong>Unused Credits:</strong> Credits left unused at the end of a billing 
          cycle (for monthly plans) do not roll over and are not eligible for a cash refund.
        </li>
      </ul>

      <h3>B. The "Try Before You Buy" Guarantee</h3>
      <p>
        To ensure you are confident in your purchase, we provide a <strong>Starter (Free) 
        Plan</strong>. This plan provides 3 Free Daily Credits to every user. We strongly 
        encourage you to use these free credits to evaluate the quality and capability 
        of our engine before upgrading to a paid subscription.
      </p>

      <h2>5. Technical Failures & Exceptions</h2>
      <p>While we strive for 100% uptime, technical errors may occur.</p>
      <ul>
        <li>
          <strong>System Failures:</strong> If our server fails to generate an output 
          due to an internal error (e.g., a timeout or server crash) but credits were 
          deducted, these credits can be returned to your account balance. Please 
          contact support as soon as possible; if we verify the error is genuine, we 
          will restore the credits.
        </li>
        <li>
          <strong>Payment Errors:</strong> If you are charged by our payment gateway 
          (Cashfree) but do not receive the corresponding subscription or credits 
          within 1 hour, please contact us immediately.
        </li>
      </ul>
      <p>
        Upon verification, we will manually credit your account or issue a refund to 
        the original payment method within 5-7 business days.
      </p>

      <h2>6. Account Deletion & Data Forfeiture</h2>
      <p>
        We respect your right to privacy and data control. You may delete your account 
        permanently via the "Danger Zone" in your Account Settings.
      </p>
      <ul>
        <li>
          <strong>Forfeiture:</strong> Upon confirming deletion, all remaining credits, 
          active subscriptions, and stored data (images/mockups) are permanently 
          deleted from our database and storage servers.
        </li>
        <li>
          <strong>No Refunds:</strong> Account deletion is irreversible. No refunds will 
          be issued for forfeited credits or subscription time upon voluntary deletion.
        </li>
      </ul>

      <h2>7. Shipping Policy</h2>
      <p>FullMockup deals exclusively in digital products.</p>
      <ul>
        <li>No physical goods (t-shirts, apparel, or prints) are shipped to the user.</li>
        <li>All generated mockups are available for instant download via the browser.</li>
      </ul>

      <h2>8. Contact Us</h2>
      <p>
        If you have questions regarding a charge or believe you are eligible for a refund 
        due to a technical error, please contact our support team.
      </p>
      <div className="contact-details-box" style={{ 
        border: '1px solid #5F4729', 
        padding: '20px', 
        borderRadius: '8px',
        margin: '15px 0',
        backgroundColor: '#ffffff'
      }}>
        <p><strong>Email:</strong> [support@fullmockup.com]</p>
        <p><strong>Phone:</strong> [Your Business Phone Number]</p>
        <p><strong>Address:</strong> [Your Registered Business Address]</p>
      </div>
      <p>
        Alternatively, you may use our <Link to="/contact-us">Official Contact Form</Link>.
      </p>
    </div>
  );
};

export default RefundPage;